<!-- components/Survey/questions/Ranking.svelte -->
<script>
  import { createEventDispatcher } from 'svelte';
  import { fly } from 'svelte/transition';
  import { flip } from 'svelte/animate';
  import { quintOut } from 'svelte/easing';
  
  export let options = [];
  export let currentValue = {};
  
  const dispatch = createEventDispatcher();
  
  // Copy options to a sortable array
  let rankableOptions = [...options];
  let localRanking = {};
  let lastSourceKey = '';
  
  // State for drag and drop
  let draggedItem = null;
  let draggedOverItem = null;
  
  function buildDefaultRanking() {
    const defaults = {};
    options.forEach((option, index) => {
      defaults[option.id] = index + 1;
    });
    return defaults;
  }

  function buildSourceRanking() {
    const hasPersistedRanking =
      currentValue &&
      typeof currentValue === 'object' &&
      !Array.isArray(currentValue) &&
      Object.keys(currentValue).length === options.length;

    return hasPersistedRanking ? { ...currentValue } : buildDefaultRanking();
  }

  // Sync local ranking only when incoming props change
  $: {
    const sourceKey = JSON.stringify({
      optionIds: options.map(option => option.id),
      currentValue
    });

    if (sourceKey !== lastSourceKey) {
      lastSourceKey = sourceKey;
      localRanking = buildSourceRanking();
    }
  }

  // Re-sort whenever local ranking changes
  $: {
    // Sort rankableOptions based on current ranks
    rankableOptions = [...options].sort((a, b) => 
      (localRanking[a.id] || 999) - (localRanking[b.id] || 999)
    );
  }

  // Drag event handlers
  function handleDragStart(e, item, index) {
    draggedItem = { item, index };
    e.dataTransfer.effectAllowed = 'move';
    
    // Add a class to style the dragged item
    setTimeout(() => {
      e.target.classList.add('dragging');
    }, 0);
  }
  
  function handleDragEnd(e) {
    e.target.classList.remove('dragging');
    draggedItem = null;
    draggedOverItem = null;
    
    // Remove all drag feedback styling
    document.querySelectorAll('.rank-item').forEach(item => {
      item.classList.remove('drag-over');
    });
  }
  
  function handleDragOver(e, targetItem, targetIndex) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Apply visual feedback only if hovering over a different item
    if (draggedItem && draggedItem.index !== targetIndex) {
      draggedOverItem = { item: targetItem, index: targetIndex };
      
      // Clear previous visual feedback
      document.querySelectorAll('.rank-item').forEach(item => {
        item.classList.remove('drag-over');
      });
      
      // Add visual feedback to the target
      e.currentTarget.classList.add('drag-over');
    }
  }
  
  function handleDrop(e, targetItem, targetIndex) {
    e.preventDefault();
    
    if (!draggedItem) return;
    
    const sourceIndex = draggedItem.index;
    const sourceItem = draggedItem.item;
    
    // Only reorder if dropping on a different item
    if (sourceIndex !== targetIndex) {
      // Determine whether we're moving up or down
      const movingUp = sourceIndex > targetIndex;
      
      // Create a copy of the ranking to modify
      const newRanking = {...localRanking};
      
      // Get the ranks we're swapping between
      const sourceRank = newRanking[sourceItem.id];
      const targetRank = newRanking[targetItem.id];
      
      // If moving up, shift all items between target and source down
      if (movingUp) {
        for (let option of rankableOptions) {
          const rank = newRanking[option.id];
          if (rank >= targetRank && rank < sourceRank) {
            newRanking[option.id] = rank + 1;
          }
        }
      } 
      // If moving down, shift all items between source and target up
      else {
        for (let option of rankableOptions) {
          const rank = newRanking[option.id];
          if (rank > sourceRank && rank <= targetRank) {
            newRanking[option.id] = rank - 1;
          }
        }
      }
      
      // Set the source item to the target rank
      newRanking[sourceItem.id] = movingUp ? targetRank : targetRank;
      
      // Update the current value
      localRanking = newRanking;
      
      // Resort the list
      rankableOptions = [...options].sort((a, b) => 
        (localRanking[a.id] || 999) - (localRanking[b.id] || 999)
      );
    }
    
    // Reset drag state
    draggedItem = null;
    draggedOverItem = null;
    
    // Clean up styles
    document.querySelectorAll('.rank-item').forEach(item => {
      item.classList.remove('drag-over');
    });
  }
  
  // Move an item up in rank (keep for accessibility and mobile support)
  function moveUp(index) {
    if (index <= 0) return;
    
    const optionA = rankableOptions[index];
    const optionB = rankableOptions[index - 1];
    
    // Swap ranks
    const tempRank = localRanking[optionA.id];
    localRanking[optionA.id] = localRanking[optionB.id];
    localRanking[optionB.id] = tempRank;
    
    // Update the sorted list
    rankableOptions = [...options].sort((a, b) => 
      (localRanking[a.id] || 999) - (localRanking[b.id] || 999)
    );
  }
  
  // Move an item down in rank (keep for accessibility and mobile support)
  function moveDown(index) {
    if (index >= rankableOptions.length - 1) return;
    
    const optionA = rankableOptions[index];
    const optionB = rankableOptions[index + 1];
    
    // Swap ranks
    const tempRank = localRanking[optionA.id];
    localRanking[optionA.id] = localRanking[optionB.id];
    localRanking[optionB.id] = tempRank;
    
    // Update the sorted list
    rankableOptions = [...options].sort((a, b) => 
      (localRanking[a.id] || 999) - (localRanking[b.id] || 999)
    );
  }
  
  // Function to send current value to parent
  function handleContinue() {
    // Create a copy to avoid reference issues
    const result = {...localRanking};
    
    // Log the values being sent (for debugging)
    console.log("Sending ranking values:", result);
    
    // Dispatch the event with the ranking data
    dispatch('rank', {
      ranking: result,
      answered: true
    });
  }
</script>

<div class="ranking">
  <!-- Title and description are handled by the parent Question component -->
  
  <div class="items-container">
    {#each rankableOptions as option, i (option.id)}
      <div 
        class="rank-item"
        animate:flip={{duration: 300}}
        in:fly={{y: 20, delay: i * 100, duration: 400, easing: quintOut}}
        draggable={true}
        on:dragstart={(e) => handleDragStart(e, option, i)}
        on:dragend={handleDragEnd}
        on:dragover={(e) => handleDragOver(e, option, i)}
        on:drop={(e) => handleDrop(e, option, i)}
      >
        <div class="rank-number">{i + 1}</div>
        <div class="rank-content">
          <div class="rank-label">{option.label}</div>
        </div>
        <div class="drag-handle">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M3,15V13H5V15H3M3,11V9H5V11H3M7,15V13H9V15H7M7,11V9H9V11H7M11,15V13H13V15H11M11,11V9H13V11H11M15,15V13H17V15H15M15,11V9H17V11H15M19,15V13H21V15H19M19,11V9H21V11H19Z"></path>
          </svg>
        </div>
        
        <!-- Keep arrow controls for mobile/accessibility -->
        <div class="rank-controls">
          <button 
            class="rank-button up" 
            disabled={i === 0}
            on:click={() => moveUp(i)}
            aria-label="Move up"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z"></path>
            </svg>
          </button>
          <button 
            class="rank-button down" 
            disabled={i === rankableOptions.length - 1}
            on:click={() => moveDown(i)}
            aria-label="Move down"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"></path>
            </svg>
          </button>
        </div>
      </div>
    {/each}
  </div>
  
  <div class="action-container">
    <p class="hint">Drag items to reorder or use the arrows to adjust rankings.</p>
    <button class="continue-button" on:click={handleContinue}>
      Continue
    </button>
  </div>
</div>

<style>
  .ranking {
    width: 100%;
  }
  
  .items-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 2rem;
  }
  
  .rank-item {
    display: flex;
    align-items: center;
    padding: 1rem;
    background-color: white;
    border-radius: 8px;
    border: 2px solid #e0e0e0;
    transition: all 0.2s ease;
    position: relative;
    cursor: grab;
  }
  
  .rank-item:hover {
    border-color: #FFB81C; /* Monsters gold color */
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .rank-item.dragging {
    opacity: 0.6;
    border-style: dashed;
  }
  
  .rank-item.drag-over {
    border-color: #FFB81C; /* Monsters blue color */
    border-style: dashed;
    box-shadow: 0 0 0 2px rgba(0, 0, 134, 0.2);
  }
  
  .rank-number {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: #FFB81C; /* Monsters blue color */
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    margin-right: 1rem;
    flex-shrink: 0;
  }
  
  .rank-content {
    flex: 1;
  }
  
  .rank-label {
    font-size: 1rem;
    color: #333;
    font-weight: 500;
  }
  
  .drag-handle {
    margin-right: 0.5rem;
    cursor: grab;
    display: flex;
    align-items: center;
    color: #999;
  }
  
  .drag-handle:hover {
    color: #666;
  }
  
  .drag-handle svg {
    fill: currentColor;
  }
  
  .rank-controls {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .rank-button {
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
    padding: 0.25rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }
  
  .rank-button:hover:not(:disabled) {
    background-color: #f5f5f5;
    color: #FFB81C;
  }
  
  .rank-button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  .rank-button svg {
    fill: currentColor;
  }
  
  .action-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  
  .hint {
    font-size: 0.9rem;
    color: #888;
    font-style: italic;
  }
  
  .continue-button {
    background-color: #cccccc; /* Monsters blue color */
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    font-size: 1.1rem;
    border-radius: 30px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-weight: 500;
  }
  
  .continue-button:hover {
    background-color: #6F263D;
  }
  
  @media (max-width: 768px) {
    .rank-item {
      padding: 0.75rem;
    }
    
    .rank-number {
      width: 25px;
      height: 25px;
      font-size: 0.9rem;
    }
    
    .rank-label {
      font-size: 0.9rem;
    }
  }
</style>
